/**
 * IconSprite — one definition per repeated inline icon, for the whole document.
 *
 * FRIDGE-179. The star-rating glyph was emitted as a complete inline <svg>,
 * path data and all, once per star. On the homepage that is 152 product cards
 * x 5 stars = 760 copies of the same 477-byte path: 364 KB of the page's
 * 1.25 MB of markup, 97% of all inline-SVG bytes, and ~750 surplus DOM nodes.
 *
 * Brotli hides those bytes from the network but not from the browser's parser,
 * the DOM node count, main-thread layout time — or from GPTBot, ClaudeBot and
 * PerplexityBot, which parse this markup to decide what to cite.
 *
 * The fix is the standard one: define each shape once in a <defs> block and
 * reference it with <use href="#id">. ~477 bytes once plus ~40 bytes per
 * instance, instead of ~477 bytes per instance.
 *
 * Mounted once in app/layout.tsx, so every route has the definitions and any
 * component can reference them without importing anything. Kept out of the
 * accessibility tree and out of layout: aria-hidden, zero size, absolute.
 *
 * Adding a shape here is only worth it for icons that repeat many times per
 * page. A one-off icon should stay inline where it renders.
 *
 * The <defs> component lives in ./icon-sprite-defs.tsx — the one client
 * module of this family (FRIDGE-929: a server-rendered <defs> block was
 * serialised a second time into every page's RSC payload). It is re-exported
 * here so app/layout.tsx and the tests keep importing `IconSprite` from this
 * path. Everything below this comment is a plain module: the icon instances
 * are safe to render from server components.
 */

import { STAR_SYMBOL_ID, ARROW_UP_RIGHT_SYMBOL_ID, CHEVRON_DOWN_SYMBOL_ID, ARROW_RIGHT_SYMBOL_ID, CHEVRON_RIGHT_SYMBOL_ID, CHEVRON_LEFT_SYMBOL_ID, COPY_SYMBOL_ID, CALENDAR_SYMBOL_ID, MAIL_SYMBOL_ID, EXTERNAL_LINK_SYMBOL_ID, GLOBE_SYMBOL_ID, SHOPPING_BAG_SYMBOL_ID, LINK_2_SYMBOL_ID, HEART_SYMBOL_ID, HEART_FILLED_SYMBOL_ID, CHECK_SYMBOL_ID, SPRITE_SYMBOL_IDS } from './icon-symbol-ids'
export { STAR_SYMBOL_ID, ARROW_UP_RIGHT_SYMBOL_ID, CHEVRON_DOWN_SYMBOL_ID, ARROW_RIGHT_SYMBOL_ID, CHEVRON_RIGHT_SYMBOL_ID, CHEVRON_LEFT_SYMBOL_ID, COPY_SYMBOL_ID, CALENDAR_SYMBOL_ID, MAIL_SYMBOL_ID, EXTERNAL_LINK_SYMBOL_ID, GLOBE_SYMBOL_ID, SHOPPING_BAG_SYMBOL_ID, LINK_2_SYMBOL_ID, HEART_SYMBOL_ID, HEART_FILLED_SYMBOL_ID, CHECK_SYMBOL_ID, SPRITE_SYMBOL_IDS }
export { IconSprite } from './icon-sprite-defs'

/**
 * StarIcon — one star instance, referencing the shared symbol.
 *
 * Drop-in for the inline <svg><path …/></svg> pattern: same viewBox, same
 * `fill="currentColor"`, so colour still comes from the caller's text class.
 */
export function StarIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
      <use href={`#${STAR_SYMBOL_ID}`} />
    </svg>
  )
}

/**
 * ArrowUpRightIcon — the catalog-card arrow, referencing the shared symbol.
 *
 * Drop-in for both prior inline forms (the lucide <ArrowUpRight/> and the
 * hand-rolled stroke path) — same glyph, same currentColor inheritance. The
 * stroke attributes live on the symbol, so an instance is ~100 bytes instead
 * of ~180-310.
 */
export function ArrowUpRightIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} aria-hidden="true">
      <use href={`#${ARROW_UP_RIGHT_SYMBOL_ID}`} />
    </svg>
  )
}

/**
 * ChevronDownIcon — the accordion chevron, referencing the shared symbol.
 *
 * Drop-in for the lucide <ChevronDown/>: same 24×24 geometry, stroke lives on
 * the symbol, colour and transforms (e.g. group-open:rotate-180) still come
 * from the caller's classes.
 */
export function ChevronDownIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <use href={`#${CHEVRON_DOWN_SYMBOL_ID}`} />
    </svg>
  )
}

/**
 * One <use> instance of a sprite symbol, drop-in for the matching lucide
 * component: same 24×24 attributes (so an unsized instance measures 24 px,
 * exactly as lucide's does), className / style / aria props pass through,
 * colour is currentColor, geometry lives on the symbol.
 */
function SpriteIcon({ symbolId, ...rest }: React.SVGProps<SVGSVGElement> & { symbolId: string }) {
  return (
    <svg width={24} height={24} viewBox="0 0 24 24" aria-hidden="true" {...rest}>
      <use href={`#${symbolId}`} />
    </svg>
  )
}

type IconProps = Omit<React.SVGProps<SVGSVGElement>, "ref">

export function ArrowRightIcon(props: IconProps) { return <SpriteIcon symbolId={ARROW_RIGHT_SYMBOL_ID} {...props} /> }
export function ChevronRightIcon(props: IconProps) { return <SpriteIcon symbolId={CHEVRON_RIGHT_SYMBOL_ID} {...props} /> }
export function ChevronLeftIcon(props: IconProps) { return <SpriteIcon symbolId={CHEVRON_LEFT_SYMBOL_ID} {...props} /> }
export function CopyIcon(props: IconProps) { return <SpriteIcon symbolId={COPY_SYMBOL_ID} {...props} /> }
export function CalendarIcon(props: IconProps) { return <SpriteIcon symbolId={CALENDAR_SYMBOL_ID} {...props} /> }
export function MailIcon(props: IconProps) { return <SpriteIcon symbolId={MAIL_SYMBOL_ID} {...props} /> }
export function ExternalLinkIcon(props: IconProps) { return <SpriteIcon symbolId={EXTERNAL_LINK_SYMBOL_ID} {...props} /> }
export function GlobeIcon(props: IconProps) { return <SpriteIcon symbolId={GLOBE_SYMBOL_ID} {...props} /> }
export function ShoppingBagIcon(props: IconProps) { return <SpriteIcon symbolId={SHOPPING_BAG_SYMBOL_ID} {...props} /> }
export function Link2Icon(props: IconProps) { return <SpriteIcon symbolId={LINK_2_SYMBOL_ID} {...props} /> }
